import { formatDate, queryDateRangePicker } from './date-range-picker';
import { updateNewsListFromAjax, type NewsListAjaxUpdateConfig } from './news-list-ajax-update';
import { querySearchInput, shouldSubmitSearchQuery } from './search-input';

export type NewsFiltersOptions = {
  searchInputId?: string;
  includeActivityFilters?: boolean;
  preserveView?: boolean;
  debounceMs?: number;
  ajax?: NewsListAjaxUpdateConfig;
};

function debounce(fn: () => void, delay: number): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;

  return () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(fn, delay);
  };
}

function getActiveFilterKeys(options: NewsFiltersOptions): string[] {
  const keys: string[] = ['q', 'date_from', 'date_to'];

  if (options.includeActivityFilters) {
    keys.push('activity_format', 'activity_type');
  }

  if (options.preserveView) {
    keys.push('view');
  }

  return keys;
}

function getCurrentFilterValue(key: string): string {
  const current = new URLSearchParams(window.location.search);

  if (key === 'q') {
    return current.get('q') ?? current.get('search') ?? '';
  }

  return current.get(key) ?? '';
}

export function filtersMatchCurrentUrl(
  params: URLSearchParams,
  options: NewsFiltersOptions = {},
): boolean {
  const keys = getActiveFilterKeys(options);

  return keys.every((key) => (params.get(key) ?? '') === getCurrentFilterValue(key));
}

export function buildNewsFiltersParams(
  root: ParentNode,
  options: NewsFiltersOptions = {},
): URLSearchParams {
  const params = new URLSearchParams();
  const search = querySearchInput(root, options.searchInputId ?? 'news-search');
  const range = queryDateRangePicker(root);

  const query = search?.input.value.trim() ?? '';
  const from = range ? formatDate(range.inputFrom.value) : '';
  const to = range ? formatDate(range.inputTo.value) : '';

  if (query) params.set('q', query);
  if (from) params.set('date_from', from);
  if (to) params.set('date_to', to);

  if (options.includeActivityFilters) {
    const activityFormat = root.querySelector<HTMLSelectElement>('#activity_format')?.value ?? '';
    const activityType = root.querySelector<HTMLSelectElement>('#activity_type')?.value ?? '';

    if (activityFormat) params.set('activity_format', activityFormat);
    if (activityType) params.set('activity_type', activityType);
  }

  if (options.preserveView) {
    const view = root.querySelector<HTMLElement>('[data-events-list]')?.dataset.eventsView;
    if (view === 'timeline') params.set('view', view);
  }

  return params;
}

export function navigateWithNewsFilters(params: URLSearchParams): void {
  const queryString = params.toString();
  window.location.href = queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname;
}

export async function submitNewsFilters(
  root: ParentNode,
  options: NewsFiltersOptions = {},
): Promise<void> {
  const params = buildNewsFiltersParams(root, options);

  if (filtersMatchCurrentUrl(params, options)) {
    return;
  }

  if (options.ajax) {
    await updateNewsListFromAjax(root, params, options.ajax);
    return;
  }

  navigateWithNewsFilters(params);
}

export function initNewsFiltersAutoSubmit(
  root: ParentNode,
  options: NewsFiltersOptions = {},
): void {
  const scope = root instanceof HTMLElement ? root : null;
  const initFlag = 'newsFiltersAutoBound';

  if (scope?.dataset[initFlag] === 'true') {
    return;
  }
  if (scope) {
    scope.dataset[initFlag] = 'true';
  }

  let ready = false;
  let lastSubmittedSearch = '';
  const debounceMs = options.debounceMs ?? 400;
  const submitNow = (): void => {
    if (!ready) return;

    const search = querySearchInput(root, options.searchInputId ?? 'news-search');
    const value = search?.input.value ?? '';

    if (!shouldSubmitSearchQuery(value, lastSubmittedSearch)) {
      return;
    }

    lastSubmittedSearch = value;
    void submitNewsFilters(root, options);
  };
  const submitDebounced = debounce(submitNow, debounceMs);

  const search = querySearchInput(root, options.searchInputId ?? 'news-search');
  if (search) {
    search.input.addEventListener('input', submitDebounced);
    search.clearBtn.addEventListener('click', () => {
      queueMicrotask(submitNow);
    });
  }

  root.addEventListener('date-range-change', submitNow);

  if (options.includeActivityFilters) {
    const activityFormat = root.querySelector<HTMLSelectElement>('#activity_format');
    const activityType = root.querySelector<HTMLSelectElement>('#activity_type');

    activityFormat?.addEventListener('change', submitNow);
    activityType?.addEventListener('change', submitNow);
  }

  queueMicrotask(() => {
    ready = true;
  });
}
